/**
 * PassengersPanel — admin Control Room → Customers tab.
 *
 * Dedicated, discoverable operator surface for every registered passenger
 * after they verify their email. Shows the launch-required fields:
 * name, email, phone, email-verified status, registration date, booking
 * count, and last booking — with a live search/filter by name / email /
 * phone. Corporate-account toggle + delete reuse the same parent-driven
 * callbacks as the Fleet → Passengers sub-tab (no API contract change).
 */
import React, { useMemo, useState } from "react";
import { Search, Briefcase, Trash2, CheckCircle2, XCircle, Users, Pencil } from "lucide-react";
import { CustomerEditModal } from "./CustomerEditModal";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return "—"; }
};

// Phone-booking customers get a synthetic "<digits>@phone.local" email
// (they never log in) — show a friendly label instead of the placeholder.
const displayEmail = (p) =>
  (p.email && p.email.endsWith("@phone.local")) || p.created_by_dispatch
    ? "Phone booking"
    : (p.email || "—");

export const PassengersPanel = ({ passengers, onToggleAccount, onDelete, onSaveCustomer }) => {
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return passengers;
    return passengers.filter((p) =>
      (p.name || "").toLowerCase().includes(q) ||
      (p.email || "").toLowerCase().includes(q) ||
      (p.phone || "").toLowerCase().includes(q)
    );
  }, [passengers, query]);

  const verifiedCount = useMemo(
    () => passengers.filter((p) => p.email_verified).length,
    [passengers]
  );

  return (
    <div className="bg-white" data-testid="passengers-panel">
      <div className="px-4 py-4 border-b border-border">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <h2 className="font-heading text-lg font-black tracking-tight flex items-center gap-2">
              <Users className="w-4 h-4" /> Customers
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5" data-testid="passengers-summary">
              {passengers.length} registered · {verifiedCount} email-verified
            </p>
          </div>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email or phone…"
            className="w-full pl-9 pr-3 py-2 border border-border text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
            data-testid="passengers-search-input"
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-sm text-muted-foreground" data-testid="passengers-empty">
          {passengers.length === 0 ? "No customers registered yet." : "No customers match your search."}
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm" data-testid="passengers-table">
              <thead>
                <tr className="text-left label-mini text-muted-foreground border-b border-border">
                  <th className="px-4 py-2 font-semibold">Name</th>
                  <th className="px-4 py-2 font-semibold">Email</th>
                  <th className="px-4 py-2 font-semibold">Phone</th>
                  <th className="px-4 py-2 font-semibold">Verified</th>
                  <th className="px-4 py-2 font-semibold">Registered</th>
                  <th className="px-4 py-2 font-semibold text-right">Bookings</th>
                  <th className="px-4 py-2 font-semibold">Last booking</th>
                  <th className="px-4 py-2 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className="border-b border-border hover:bg-secondary/30" data-testid={`passenger-table-row-${p.id}`}>
                    <td className="px-4 py-2.5 font-semibold whitespace-nowrap">
                      <span className="flex items-center gap-2">
                        {p.name}
                        {p.is_account_customer && (
                          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-[#EFF3FF] text-[#002FA7] border border-[#002FA7]" data-testid={`passenger-account-badge-${p.id}`}>
                            <Briefcase className="w-2.5 h-2.5" /> ACCOUNT
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">{displayEmail(p)}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap" data-testid={`passenger-phone-${p.id}`}>{p.phone || "—"}</td>
                    <td className="px-4 py-2.5" data-testid={`passenger-verified-${p.id}`}>
                      {p.email_verified ? (
                        <span className="inline-flex items-center gap-1 text-[#065F46]"><CheckCircle2 className="w-3.5 h-3.5" /> Verified</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[#92400E]"><XCircle className="w-3.5 h-3.5" /> Unverified</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-muted-foreground">{fmtDate(p.created_at)}</td>
                    <td className="px-4 py-2.5 text-right font-semibold" data-testid={`passenger-bookings-${p.id}`}>{p.booking_count ?? 0}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-muted-foreground">{fmtDate(p.last_booking_at)}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setEditing(p)}
                          className="text-xs px-2 py-1 border border-border hover:bg-secondary transition-colors"
                          data-testid={`passenger-edit-${p.id}`}
                          title="Edit customer"
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => onToggleAccount(p)}
                          className={`text-xs px-2 py-1 border transition-colors ${p.is_account_customer ? "bg-[#002FA7] text-white border-[#002FA7] hover:bg-[#001f6e]" : "border-border hover:bg-secondary"}`}
                          data-testid={`passenger-toggle-account-${p.id}`}
                          title="Toggle corporate account"
                        >
                          <Briefcase className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => onDelete(p.id, "passenger")}
                          className="text-muted-foreground hover:text-destructive p-1.5"
                          data-testid={`passenger-delete-${p.id}`}
                          title="Delete passenger"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden p-4 space-y-2">
            {filtered.map((p) => (
              <div key={p.id} className="border border-border p-3" data-testid={`passenger-card-${p.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm flex items-center gap-2">
                      {p.name}
                      {p.is_account_customer && (
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-[#EFF3FF] text-[#002FA7] border border-[#002FA7]">
                          <Briefcase className="w-2.5 h-2.5" /> ACCOUNT
                        </span>
                      )}
                    </div>
                    <div className="label-mini text-muted-foreground truncate">{displayEmail(p)}</div>
                    <div className="label-mini text-muted-foreground">{p.phone || "—"}</div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => setEditing(p)} className="text-xs px-2 py-1 border border-border" data-testid={`passenger-edit-${p.id}`} title="Edit customer">
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button onClick={() => onToggleAccount(p)} className={`text-xs px-2 py-1 border ${p.is_account_customer ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-border"}`} data-testid={`passenger-toggle-account-${p.id}`}>
                      <Briefcase className="w-3 h-3" />
                    </button>
                    <button onClick={() => onDelete(p.id, "passenger")} className="text-muted-foreground hover:text-destructive p-1.5" data-testid={`passenger-delete-${p.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                  <div><span className="label-mini text-muted-foreground">Verified</span><div>{p.email_verified ? "Yes" : "No"}</div></div>
                  <div><span className="label-mini text-muted-foreground">Registered</span><div>{fmtDate(p.created_at)}</div></div>
                  <div><span className="label-mini text-muted-foreground">Bookings</span><div className="font-semibold">{p.booking_count ?? 0}</div></div>
                  <div><span className="label-mini text-muted-foreground">Last booking</span><div>{fmtDate(p.last_booking_at)}</div></div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {editing && (
        <CustomerEditModal
          customer={editing}
          onClose={() => setEditing(null)}
          onSave={onSaveCustomer}
        />
      )}
    </div>
  );
};

export default PassengersPanel;
